import * as Phaser from "phaser-ce"
import { COLOR } from "../../../common/Color"
import { log } from "../../../common/Logging"
import { FontType } from "../../../common/TextUtils"
import { GameWithScore, StudentResultsStore } from "../../../react/students/StudentResultsStore"
import { ActivityKey } from "../../../shared/models/ActivityKey"
import { TransitionState } from "../../GameState"
import { BackgroundSprite } from "../../generic-sprites/BackgroundSprite"
import { Buttons } from "../../generic-sprites/Buttons"
import { colorNum, ColorUtils } from "../../utils/ColorUtils"
import { PhaserText } from "../../utils/PhaserText"
import { PhaserUtils } from "../../utils/PhaserUtils"
import { Scale } from "../../utils/Scale"
import { Sprites } from "../../utils/Sprites"
import { Transitions } from "../transitions/Transitions"
import { HeroAssets, loadAssets } from "./HeroAssets"
import { HeroPos } from "./HeroPos"
import { HeroScore } from "./HeroScore"
import { HeroStore } from "./HeroStore"

export class HeroStartState extends Phaser.State {

    static readonly LOW_SCORE_THRESHOLD = 100
    static readonly MEDIUM_SCORE_THRESHOLD = 200

    preload() {
        loadAssets(this.game)
    }

    async create() {
        await PhaserUtils.sharedCreateFunctions(this.game)
        if (!PhaserUtils.loggedIn()) {
            PhaserUtils.notLoggedInMessage(this.game)
            return
        }
        ColorUtils.setBackgroundColorGray(this.game)
        this.game.add.existing(new BackgroundSprite(this.game, HeroAssets.ENTER_SCREEN))

        const gamesPlayed = HeroStore.getTimesPlayed()
        const allButtons: Array<Phaser.Button> = []
        const startButtons = this.game.add.group()
        const playAgainButtons = this.game.add.group()

        const start = new Phaser.Button(this.game, 0, HeroPos.buttonY(), HeroAssets.PLAY_BUTTON)
        startButtons.add(start)
        allButtons.push(start)

        const playAgain = new Phaser.Button(this.game, HeroPos.easyButtonX(), HeroPos.buttonY(),
            HeroAssets.PLAY_AGAIN_BUTTON)
        playAgainButtons.add(playAgain)
        allButtons.push(playAgain)

        const highScores = new Phaser.Button(this.game, HeroPos.mediumButtonX(), HeroPos.buttonY(),
            HeroAssets.HIGH_SCORES_BUTTON)
        playAgainButtons.add(highScores)
        allButtons.push(highScores)

        const quit = new Phaser.Button(this.game, HeroPos.hardButtonX(), HeroPos.buttonY(), HeroAssets.QUIT_BUTTON)
        playAgainButtons.add(quit)
        allButtons.push(quit)

        allButtons.forEach((button) => {
            Scale.scaleSprite(button, 0.78)
            button.tint = colorNum.gray
            button.inputEnabled = false
        }, this)
        Sprites.centerInWorldXOnly(start)

        const audio = this.determineAudio()
        let boardText: string
        // TODO delete the audio clipping when we get the fixed clip.
        if (HeroStore.getTimesPlayed() === 0) {
            boardText = "SUPER HERO"
            audio.addMarker("clipped", 0, 18)
            audio.play("clipped")
        } else {
            boardText = "SCOREBOARD"
            audio.play()
        }

        const title = PhaserText.create(this.game, boardText, {y: HeroPos.titleY(), fontPx: 78, type: FontType.TITLE})
        Sprites.centerInWorldXOnly(title)

        if (gamesPlayed === 0) {
            playAgainButtons.visible = false
            this.addInfo("It's time to save the people at this party from secondhand smoke and vapor!\n\n" +
                "Click and drag the cigarettes and e-cigarettes to the trash.\n\n" +
                "You have one minute to keep everyone from getting sick.")
        } else {
            startButtons.visible = false
            const score = HeroStore.getScore()
            this.addInfo("You scored " + score + " points" + (score === 0 ? "." : "!") + "\n" +
                "You're a " + HeroScore.getDescription(score))
        }

        const enableDiffButtons = () => {
            startButtons.forEach((button) => {
                button.tint = colorNum.white
                Buttons.makeSpriteButton(button)
            }, this)
            playAgainButtons.visible = false
            startButtons.visible = true
        }

        audio.onStop.addOnce(() => {
            if (gamesPlayed === 0) {
                enableDiffButtons()
            } else {
                highScores.tint = colorNum.white
                Buttons.makeSpriteButton(highScores)
                if (gamesPlayed > 2) {
                    quit.tint = colorNum.white
                    Buttons.makeSpriteButton(quit)
                } else {
                    playAgain.tint = colorNum.white
                    Buttons.makeSpriteButton(playAgain)
                }
            }
        })

        start.events.onInputDown.add(() => {
            this.game.state.start(TransitionState.SUPER_HERO_CORE)
        })
        playAgain.events.onInputDown.add(() => {
            this.game.state.start(TransitionState.SUPER_HERO_CORE)
        })
        highScores.events.onInputDown.add(() => {
            highScores.inputEnabled = false
            highScores.tint = colorNum.gray
            this.displayScores()
        })
        quit.events.onInputDown.add(() => {
            HeroStore.clearRepo()
            Transitions.startOutro(this.game, ActivityKey.SUPER_HERO)
        })
    }

    displayScores = () => {
        const scoresGroup = this.game.add.group()
        const scorePanel = scoresGroup.create(0, HeroPos.scorePanelY(), HeroAssets.SCORE_SCREEN)
        Scale.scaleSprite(scorePanel, 0.4)
        Sprites.centerInWorldXOnly(scorePanel)
        let scoresText = ""
        const scores = HeroStore.getScores()
        scores.forEach((score: HeroScore) => {
            scoresText += "Game " + score.gameCount + " = " + score.score + " points. "
                + HeroScore.getDescription(score.score) + "\n"
        })

        scoresText += "\n"
        scoresText += "Recent Scores\n\n"

        scoresText += StudentResultsStore.instance.findRecentScores(GameWithScore.SUPER_HERO)
                                         .slice(0, 5)
                                         .map((score) => `${score.screenname} = ${score.score} points.\n`)
                                         .join("")

        PhaserText.create(this.game, scoresText, {
            x: HeroPos.scoresX(), y: HeroPos.scoresY(), fontPx: 14, type: FontType.MONOSPACE, color: COLOR.neonGreen,
            group: scoresGroup
        })
    }

    determineAudio = (): Phaser.Sound => {
        let audio: Phaser.Sound
        const timesPlayed = HeroStore.getTimesPlayed()
        const score = HeroStore.getScore()
        log.debug("Determining audio. TimesPlayed " + timesPlayed + " score " + score)
        if (timesPlayed === 0) {
            audio = this.game.add.audio(HeroAssets.INTRO)
        } else if (timesPlayed === 1) {
            if (score < HeroStartState.LOW_SCORE_THRESHOLD) {
                audio = this.game.add.audio(HeroAssets.GAME_1_NO_CIGS)
            } else if (score < HeroStartState.MEDIUM_SCORE_THRESHOLD) {
                audio = this.game.add.audio(HeroAssets.GAME_1_SOME_CIGS)
            } else {
                audio = this.game.add.audio(HeroAssets.GAME_1_ALL_CIGS)
            }
        } else if (timesPlayed === 2) {
            if (score < HeroStartState.LOW_SCORE_THRESHOLD) {
                audio = this.game.add.audio(HeroAssets.GAME_2_NO_CIGS)
            } else if (score < HeroStartState.MEDIUM_SCORE_THRESHOLD) {
                audio = this.game.add.audio(HeroAssets.GAME_2_SOME_CIGS)
            } else {
                audio = this.game.add.audio(HeroAssets.GAME_2_ALL_CIGS)
            }
        } else {
            if (score < HeroStartState.LOW_SCORE_THRESHOLD) {
                audio = this.game.add.audio(HeroAssets.GAME_3_NO_CIGS)
            } else if (score < HeroStartState.MEDIUM_SCORE_THRESHOLD) {
                audio = this.game.add.audio(HeroAssets.GAME_3_SOME_CIGS)
            } else {
                audio = this.game.add.audio(HeroAssets.GAME_3_ALL_CIGS)
            }
        }

        return audio
    }

    addInfo = (info: string, group?: Phaser.Group): Phaser.Text => {
        const infoText = PhaserText.create(this.game, info, {
            y: HeroPos.directionsY(), fontPx: 18, wordWrapWidth: 0.396, group
        })
        Sprites.centerInWorldXOnly(infoText)
        return infoText
    }

}
